import { EventSourceMessage, fetchEventSource } from '@microsoft/fetch-event-source';

import config from '../../config';
import {
  InboxMessage,
  InboxPageRequest,
  InboxPageResponse,
  MarkAsReadByMetadata,
  SummaryResponse,
  UnreadCountPayload,
  UnreadCountResponse,
} from '../models/ams/types';
import { getStringLiteral } from '../util/DataHelpers';

import { DynamicFilterBuilder } from './filter/dynamic-filter-builder';
import { FilterType } from './filter/types';
import { getUserEventsLastEventId, LAST_EVENT_ID_HEADER } from './user-events/last-event-storage';
import { USER_EVENT_TYPES, UserEventsStreamHandlers, UserSSEEvent } from './user-events/sse-event-types';
import { BaseRequestService } from './BaseRequest.service';
import { IAMSService } from './types';

const API_PATH = {
  messages: '/messages/inbox',
  getSummary: '/messages/inbox/summary',
  markAllAsRead: '/messages/inbox/mark-all-as-read',
  markAsRead: '/messages/inbox/mark-as-read?ids=$1',
  markAsReadByMetadata: '/messages/inbox/mark-as-read-by-metadata',
  notifyUnreadCount: '/messages/inbox/notify-unread-count',
  userEvents: '/sse/users/me',
};

export class AMSService extends BaseRequestService implements IAMSService {
  protected getBaseUrl(): string {
    return config.ams.url;
  }

  constructor() {
    super();
  }

  public async getAllMessages(params: InboxPageRequest) {
    return this.api.get<InboxPageResponse, void>(API_PATH.messages, params);
  }

  public async deleteMessages(ids: number | number[]) {
    return this.api.delete<void>(API_PATH.messages, {
      ids: getStringLiteral(ids),
      ...this.getPortalIdFilter(),
    });
  }

  public async getMessage(id: number) {
    // Add caching logic in ART-4234
    return this.api.get<InboxMessage, void>(`${API_PATH.messages}/${id}`, this.getPortalIdFilter());
  }

  public async markAllMessagesAsRead() {
    return this.api.put<void>(API_PATH.markAllAsRead, this.getPortalIdFilter());
  }

  public async markAsRead(ids: number | number[]) {
    return this.api.put<void>(API_PATH.markAsRead.replace('$1', getStringLiteral(ids)), this.getPortalIdFilter());
  }

  public async notifyUnreadCount(requestBody?: UnreadCountPayload) {
    return this.api.put<UnreadCountResponse>(
      API_PATH.notifyUnreadCount,
      { ...requestBody, ...this.getPortalIdFilter() },
    );
  }

  public async getSummary() {
    return this.api.get<SummaryResponse, void>(API_PATH.getSummary, this.getPortalIdFilter());
  }

  private getPortalIdFilter(): FilterType {
    return {
      filters: new DynamicFilterBuilder().equal('customerProductId', config.portalId)
        .build(),
    };
  }

  public async markAsReadByMetadata(requestBody: MarkAsReadByMetadata) {
    return this.api.put<void>(API_PATH.markAsReadByMetadata, { ...requestBody, ...this.getPortalIdFilter() });
  }

  public streamUserEvents(handlers: UserEventsStreamHandlers): () => void {
    const { onEvent, onOpen, onError, onComplete } = handlers;
    const url = `${this.getBaseUrl()}${API_PATH.userEvents}`;

    // Resume from where the previous session stopped, if that id is still around
    const headers: Record<string, string> = { 'X-CSRF': '1' };
    const lastEventId = getUserEventsLastEventId();

    if (lastEventId) {
      headers[LAST_EVENT_ID_HEADER] = lastEventId;
    }

    const abortController = new AbortController();

    // Guard so onComplete fires exactly once regardless of which path closes the stream.
    let closed = false;
    const notifyClose = () => {
      if (closed) return;
      closed = true;
      onComplete?.();
    };

    try {
      fetchEventSource(url, {
        headers,
        credentials: 'include',
        signal: abortController.signal,
        openWhenHidden: true,
        async onopen(response: Response) {
          if (response.ok) {
            onOpen?.();
            return; // Success
          }

          const errorMessage = `HTTP ${response.status}: ${response.statusText}`;
          console.error('[AMSService] User events SSE connection failed:', errorMessage);
          throw new Error(errorMessage);
        },
        onmessage(event: EventSourceMessage) {
          // Keep-alive frames are named, so they can be dropped before looking at the payload
          if (event.event === USER_EVENT_TYPES.HEARTBEAT) {
            return;
          }

          try {
            // Handle potential SSE protocol prefix (defensive)
            let jsonData = event.data;

            // Check if data starts with "data: " prefix (malformed SSE)
            if (jsonData.startsWith('data:')) {
              jsonData = jsonData.substring(5).trim();
            }

            // Skip empty messages
            if (!jsonData || jsonData.trim().length === 0) {
              return;
            }

            // Any other non JSON frame is unexpected - skip it instead of reporting it through
            // onError, which would wrongly flag the connection as down.
            if (!jsonData.startsWith('{') && !jsonData.startsWith('[')) {
              console.warn('[AMSService] Skipping non JSON user event:', event.event, jsonData);
              return;
            }

            // The type is the SSE event name, the payload is flat - assemble the envelope here
            const userEvent = { id: event.id, type: event.event, data: JSON.parse(jsonData) } as UserSSEEvent;

            onEvent(userEvent);
          } catch (error) {
            console.error('[AMSService] Error parsing user events SSE message:', error);
            onError?.(error as Error);
          }
        },
        onerror(error: any) {
          throw error;
        },
        onclose() {
          notifyClose();
        },
      } as any).catch((err: Error) => {
        onError?.(err);
        notifyClose();
      });
    } catch (error) {
      console.error('[AMSService] Failed to initiate user events SSE connection:', error);
      onError?.(error as Error);
      notifyClose();
    }

    // Return cleanup function
    return () => {
      abortController.abort();
      notifyClose();
    };
  }
}
